import type {
  ArticleFull,
  ArticleSummary,
  Category,
  Comment,
} from '../../shared/types.ts';

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export interface CategoryMeta { label: string; blurb: string }

export interface VisitorCounts { total: number; weekly: number }

export const api = {
  me: () =>
    http<{
      user: { id: number; username: string } | null;
      online: number;
      visitors: VisitorCounts;
      version: string;
    }>('/api/me'),

  stats: () =>
    http<{
      published: number;
      planned: number;
      total: number;
      visitors: VisitorCounts;
      version: string;
      online: number;
    }>('/api/stats'),

  register: (username: string, password: string) =>
    http<{ user: { id: number; username: string } }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  login: (username: string, password: string) =>
    http<{ user: { id: number; username: string } }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logout: () => http<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  listArticles: (params: { category?: Category; tag?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.category) q.set('category', params.category);
    if (params.tag) q.set('tag', params.tag);
    const qs = q.toString();
    return http<{ articles: ArticleSummary[]; categories: Record<Category, CategoryMeta> }>(
      `/api/articles${qs ? `?${qs}` : ''}`,
    );
  },

  listAll: () =>
    http<{ articles: ArticleSummary[]; categories: Record<Category, CategoryMeta> }>('/api/articles/all'),

  getArticle: (slug: string) =>
    http<{ article: ArticleFull; related: ArticleSummary[] }>(`/api/articles/${slug}`),

  listComments: (slug: string) =>
    http<{ comments: Comment[] }>(`/api/comments/${slug}`),

  postComment: (slug: string, body: string) =>
    http<{ comment: Comment }>(`/api/comments/${slug}`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  deleteComment: (id: number) =>
    http<{ ok: boolean }>(`/api/comments/${id}`, { method: 'DELETE' }),

  recordView: (path: string) =>
    fetch('/api/views', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
      keepalive: true,
    }).catch(() => {
      // best-effort tracking — don't surface failures to the user
    }),

  topPages: (limit = 100) =>
    http<{ pages: TopPage[] }>(`/api/views/top?limit=${limit}`),
};

export interface TopPage {
  path: string;
  title: string;
  kind: 'home' | 'category' | 'article' | 'roadmap' | 'stats' | 'other';
  views: number;
  uniques: number;
}
